import React, { useEffect, useMemo, useRef, useState } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../../shared/api/firebase/config';
import { CheckCircle, Clock, ClipboardList, LogIn } from 'lucide-react';

import { ASSETS, FIREBASE_CONFIG, LAW_FIRM_INFO } from '../../config/Config';
import { fetchClientById, findClientsByEmail, checkCpfConflict, savePortalClient } from './PortalCompletarCadastroRepository';
import { useAuth } from '../../hooks/useAuth';
import { ClientForm } from '../../features/clients/components/ClientManager';
import PortalPageHeader from '../../components/portal/PortalPageHeader';
import { isRegistrationComplete } from '../../utils/registrationUtils';
import { saveClientWithCpfIndex } from '../../services/cpfIndexService';

const appId = String(FIREBASE_CONFIG?.projectId || '').trim();

const brandTitle = String(LAW_FIRM_INFO?.brand?.name || LAW_FIRM_INFO?.name || 'Portal Jurídico').trim();


export default function PortalCompletarCadastro({ embedded = false, clientId: propClientId = '' }) {
  const { currentUser, loading: authLoading } = useAuth();
  const email = useMemo(() => String(currentUser?.email || '').trim(), [currentUser?.email]);

  const [clientId, setClientId] = useState('');
  const [clientData, setClientData] = useState(null);
  const clientIdRef = useRef('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [pageStatus, setPageStatus] = useState('idle');
  const [saveError, setSaveError] = useState('');

  // FIX CRÍTICO: Garante que o ID do perfil selecionado (via ProfileSelector)
  // seja capturado imediatamente no ref, antes do useEffect assíncrono completar.
  // Sem isso, o save falha com FALHA CRÍTICA quando propClientId muda após seleção.
  useEffect(() => {
    if (!propClientId) return;
    clientIdRef.current = propClientId;
    setClientId((prev) => prev || propClientId);
  }, [propClientId]);

  useEffect(() => {
    if (authLoading || !email) return;
    let alive = true;
    setLookupLoading(true);
    setPageStatus('idle');
    setClientId('');
    setClientData(null);

    const hydrateForm = (id, data) => {
      clientIdRef.current = id;
      setClientId(id);
      setClientData({ id, ...(data || {}) });
      if (isRegistrationComplete(data)) setPageStatus('approved');
      else setPageStatus('form');
    };

    const run = async () => {
      try {
        const storedId = propClientId || (() => {
          try { return String(window.sessionStorage.getItem('portalClientId') || '').trim(); } catch { return ''; }
        })();

        if (storedId) {
          const data = await fetchClientById(appId, storedId);
          if (!alive) return;
          if (data) {
            hydrateForm(data.id, data);
            try { window.sessionStorage.removeItem('portalClientId'); } catch {}
            return;
          }
          try { window.sessionStorage.removeItem('portalClientId'); } catch {}
        }

        const NON_HUMAN_TYPES = ['animal', 'jurídica', 'juridica', 'pj', 'espolio', 'espólio', 'cnpj', 'massa'];
        const isHuman = (data) => {
          const raw = String(data?.type || data?.nature || '').toLowerCase().trim();
          if (!raw) return true;
          return !NON_HUMAN_TYPES.some((k) => raw.includes(k));
        };

        const hits = await findClientsByEmail(appId, email);

        if (!alive) return;

        const humanHits = hits.filter(isHuman);

        if (humanHits.length > 0) {
          const best = humanHits[0];
          hydrateForm(best.id, best);
        } else {
          setClientId('');
          setPageStatus('form');
        }
      } catch {
        if (!alive) return;
        setPageStatus('form');
      } finally {
        if (alive) setLookupLoading(false);
      }
    };

    run();
    return () => { alive = false; };
  }, [authLoading, email]);

  const handlePortalSave = async (payload) => {
    setSaveError('');

    // ══ TRAVA DE MUTAÇÃO V15.0 — OPERAÇÃO ESTRITA ══
    // O portal NUNCA pode criar um novo documento — apenas atualizar o perfil autorizado.
    const safeClientId = clientIdRef.current
      || clientId
      || clientData?.id
      || String(propClientId || '').trim()
      || (() => { try { return String(window.sessionStorage.getItem('portalClientId') || '').trim(); } catch { return ''; } })();

    if (!safeClientId) {
      console.error('[RDA SECURITY] FALHA CRÍTICA: Tentativa de salvamento sem ID no Portal do Cliente.');
      setSaveError('Erro de segurança: ID do perfil não encontrado. Por favor, recarregue a página e tente novamente.');
      return;
    }

    const name = String(payload?.qualification?.physical?.socialName || payload?.qualification?.physical?.name || '').trim();
    const cpf = String(payload?.qualification?.physical?.cpf || '').replace(/\D/g, '');
    const email = String(payload?.contact?.email || '').trim();
    const whatsapp = String(payload?.contact?.phonePrimary || payload?.contact?.phone || '').replace(/\D/g, '');
    const profession = String(payload?.qualification?.physical?.profession || '').trim();
    const street = String(payload?.location?.address?.street || '').trim();
    const city = String(payload?.location?.address?.city || '').trim();
    if (!name || cpf.length < 11 || !email || whatsapp.length < 10 || !profession || !street || !city) {
      setSaveError('ERRO: Campos obrigatórios não preenchidos (Nome, CPF, Email, WhatsApp, Profissão e Endereço).');
      return;
    }

    try {
      // ══ MC2: SCANNER DE CPF — veto duplicidade antes do save ══
      if (cpf.length >= 11) {
        const conflictId = await checkCpfConflict(appId, cpf, safeClientId);
        if (conflictId) {
          console.error('[RDA SECURITY] CPF duplicado detectado. IDs:', safeClientId, conflictId);
          setSaveError('CONFLITO DE CPF: Este CPF já está vinculado a outro cadastro no escritório. Por favor, entre em contato para regularização.');
          return;
        }
      }

      // ══ PASSO 19: saveClientWithCpfIndex — Batch Write atômico + índice CPF ══
      const portalResult = await savePortalClient(appId, safeClientId, {
        ...payload,
        onboardingStatus: 'APPROVED',
        isDeleted: false,
        clientUpdated: true,
      }, cpf);
      if (!portalResult.success) {
        setSaveError('ERRO ao salvar cadastro: ' + portalResult.error);
        return;
      }
      console.log('[RDA LOG] Handshake concluído (cpfIndexService/merge) para o ID:', safeClientId);
      window.location.href = '/portal/dashboard';
    } catch (e) {
      setSaveError(String(e?.message || 'Erro ao salvar cadastro.'));
      throw e;
    }
  };

  const handlePortalCancel = async () => {
    try { await signOut(auth); } catch {}
    try { sessionStorage.clear(); } catch {}
    window.location.href = '/';
  };

  const goToDashboard = () => {
    window.location.href = '/portal/dashboard';
  };

  const Shell = ({ children }) => embedded
    ? <div className="w-full">{children}</div>
    : (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <div className="bg-[#20243B] px-6 py-4 flex items-center gap-3">
          {ASSETS?.logoIcon ? (
            <img src={ASSETS.logoIcon} alt={brandTitle} className="h-8 w-8 object-contain" />
          ) : null}
          <span className="text-white font-extrabold text-sm uppercase tracking-widest">{brandTitle}</span>
        </div>
        <div className="flex-1 flex items-start justify-center p-4 md:p-8">
          <div className="w-full max-w-2xl">{children}</div>
        </div>
      </div>
    );

  if (authLoading || lookupLoading) {
    return (
      <Shell>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 flex items-center gap-3">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-[#C5A059]" />
          <span className="text-sm font-bold text-[#20243B]">Carregando...</span>
        </div>
      </Shell>
    );
  }

  if (!email) {
    return (
      <Shell>
        <div className="bg-white rounded-2xl border border-red-200 shadow-sm p-8">
          <div className="flex items-center gap-3 mb-3">
            <LogIn className="w-5 h-5 text-red-600" />
            <span className="text-sm font-extrabold text-red-700">Sessão não identificada</span>
          </div>
          <p className="text-xs text-gray-600">
            Acesse o link de convite enviado para o seu e-mail para prosseguir.
          </p>
        </div>
      </Shell>
    );
  }

  if (pageStatus === 'approved') {
    return (
      <Shell>
        <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-8">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="w-6 h-6 text-emerald-600" />
            <span className="text-base font-extrabold text-[#20243B]">Cadastro aprovado!</span>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            Seu cadastro já foi homologado pelo escritório. Clique abaixo para acessar o portal.
          </p>
          <button
            type="button"
            onClick={goToDashboard}
            className="w-full rounded-xl bg-[#20243B] px-4 py-3 text-sm font-extrabold uppercase tracking-widest text-white hover:bg-[#1a1e30]"
          >
            Acessar Meu Portal
          </button>
        </div>
      </Shell>
    );
  }

  if (pageStatus === 'pending') {
    return (
      <Shell>
        <div className="bg-white rounded-2xl border border-amber-200 shadow-sm p-8">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="w-6 h-6 text-amber-600" />
            <span className="text-base font-extrabold text-[#20243B]">Aguardando aprovação</span>
          </div>
          <p className="text-sm text-gray-600">
            Seus dados foram enviados e estão sendo analisados pelo escritório.
            Em breve você receberá a confirmação de acesso.
          </p>
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
            Login: <strong>{email}</strong>
          </div>
        </div>
      </Shell>
    );
  }

  if (pageStatus === 'success') {
    return (
      <Shell>
        <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-8 text-center">
          <CheckCircle className="w-10 h-10 text-emerald-600 mx-auto mb-4" />
          <div className="text-base font-extrabold text-[#20243B] mb-2">Dados enviados com sucesso!</div>
          <p className="text-sm text-gray-600">
            O escritório foi notificado e revisará suas informações em breve.
            Você receberá a confirmação de acesso por e-mail.
          </p>
          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
            Login: <strong>{email}</strong>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {embedded && (
        <div className="mb-6">
          <PortalPageHeader
            title="Completar Cadastro"
            subtitle="Preencha seus dados para liberar o acesso ao portal."
          />
        </div>
      )}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {!embedded && (
          <div className="bg-[#20243B] px-6 py-5">
            <div className="flex items-center gap-3">
              <ClipboardList className="w-5 h-5 text-[#C5A059]" />
              <div>
                <div className="text-white font-extrabold text-sm uppercase tracking-widest">
                  Completar Cadastro
                </div>
                <div className="text-slate-300 text-xs mt-0.5">
                  Preencha seus dados para liberar o acesso ao portal.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="px-6 py-3 border-b border-slate-200 bg-[#C5A059]/5">
          {(() => {
            const name = String(clientData?.fullName || clientData?.qualification?.physical?.socialName || clientData?.qualification?.physical?.name || '').trim();
            return name ? <div className="text-sm font-extrabold text-[#20243B] mb-0.5">{name}</div> : null;
          })()}
          <div className="text-xs text-[#20243B]"><strong>Login:</strong> {email}</div>
        </div>

        {saveError ? (
          <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">{saveError}</div>
        ) : null}

        <div className="p-4 md:p-6">
          <ClientForm
            key={clientId || clientData?.id || 'portal-form'}
            initialData={clientData}
            onSave={handlePortalSave}
            onCancel={handlePortalCancel}
            isClientPortal={true}
          />
        </div>
      </div>
    </Shell>
  );
}
